import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * ImageUploader — reusable component
 * Props:
 *   value: current image URL (or empty string)
 *   onChange: (url) => void
 *   folder: subfolder on S3 (e.g. "logos", "banners", "services")
 *   tenantSlug: Super Admin only — store the file under this tenant's prefix
 *   shape: "square" | "wide" | "round"  — only affects preview visual
 *   label: optional caption
 *   testid: data-testid prefix
 */
const ACCEPTED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];

export default function ImageUploader({ value, onChange, folder = "uploads", tenantSlug, shape = "square", label, testid = "image-uploader" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error("Please pick a PNG, JPEG, WebP or GIF image"); return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Max 8 MB"); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      if (tenantSlug) fd.append("tenant_slug", tenantSlug);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(r.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const previewClass =
    shape === "round" ? "h-24 w-24 rounded-full"
    : shape === "wide" ? "w-full aspect-[16/9] rounded-lg"
    : "h-24 w-24 rounded-lg";

  const layoutClass = shape === "wide" ? "mt-1 space-y-3" : "mt-1 flex items-start gap-3";
  const controlsClass = shape === "wide" ? "space-y-2" : "flex-1 min-w-0 space-y-2";

  return (
    <div data-testid={testid}>
      {label && <label className="admin-overline">{label}</label>}
      <div className={layoutClass}>
        <div className={`${previewClass} bg-neutral-100 border border-neutral-200 overflow-hidden flex items-center justify-center ${shape === "wide" ? "" : "flex-shrink-0"}`}>
          {value
            ? <img src={value} alt="" className="h-full w-full object-cover" />
            : <Upload className="h-5 w-5 text-neutral-400" />
          }
        </div>
        <div className={controlsClass}>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              data-testid={`${testid}-pick`}
              className="bg-black text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-800 disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
              {uploading ? "Uploading..." : (value ? "Replace" : "Upload image")}
            </button>
            {value && (
              <button
                type="button"
                onClick={() => onChange("")}
                data-testid={`${testid}-clear`}
                className="border border-neutral-300 px-3 py-2 text-xs font-semibold uppercase tracking-wider hover:bg-neutral-50 inline-flex items-center gap-1.5"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            )}
          </div>
          <input
            ref={inputRef} type="file" accept={ACCEPTED_TYPES.join(",")} onChange={onPick}
            className="hidden" data-testid={`${testid}-input`}
          />
          <details className="text-xs text-neutral-500">
            <summary className="cursor-pointer hover:text-black">or paste an image URL</summary>
            <div className="flex gap-1 mt-2">
              <input
                value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://..."
                className="flex-1 min-w-0 border border-neutral-300 px-2 py-1.5 text-xs focus:outline-none focus:border-black"
                data-testid={`${testid}-url-input`}
              />
              <button
                type="button"
                onClick={() => { if (urlInput) { onChange(urlInput); setUrlInput(""); toast.success("URL set"); } }}
                className="bg-neutral-100 px-3 py-1.5 text-xs font-semibold hover:bg-neutral-200"
                data-testid={`${testid}-url-set`}
              >Set</button>
            </div>
          </details>
          {value && (
            <div className="text-[10px] font-mono text-neutral-400 break-anywhere">{value}</div>
          )}
        </div>
      </div>
    </div>
  );
}
